import * as React from 'react';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Container from '@mui/material/Container';
import PasswordReset from '../pages/components/PasswordReset';
import PaymentDetails from '../pages/components/PaymentDetails';
import CancelSubscription from '../pages/components/CancelSubscription';
import { isSocialConnected } from '../utils/authenticationUtils';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { UserRole } from '../types/role';

import { Grid, Box, Divider } from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

export default function AccountSettings() {
  const authContext = useContext(AuthContext);
  const role = localStorage.getItem('role');

  const isShopOwner = role === UserRole.SHOP_OWNER;
  const isBarber = role === UserRole.BARBER;
  const showPaymentAndCancellation = isShopOwner || isBarber;

  const [expanded, setExpanded] = React.useState<string | false>(false);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <Container
      sx={{
        marginTop: '75px !important',
        width: '100%',
        padding: '0px 15px !important',
      }}
    >
      <Grid sx={{ marginTop: { xs: '0px', lg: '110px' } }}>
        <Grid
          component={Box}
          item
          xs={6}
          md={4}
          lg={4}
          display={{ xs: 'none', md: 'block' }}
        />
        {/* Header Banner */}
        <Box
          sx={{
            mb: 3,
            p: 3,
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            borderRadius: 2,
            borderLeft: '5px solid #dd8709',
            color: 'white',
          }}
        >
          <Typography variant="h4" sx={{ fontFamily: 'GFS Didot' }}>
            Account
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Manage your security, payment, and subscription settings.
          </Typography>
        </Box>

        {/* ── Security section ─────────────────────────────── */}
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}
        >
          Security
        </Typography>

        {!isSocialConnected() && (
          <Accordion
            expanded={expanded === 'panel-reset'}
            onChange={handleChange('panel-reset')}
            sx={{ mb: 1 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel-reset-content"
              id="panel-reset-header"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LockResetIcon fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Reset Password
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <PasswordReset
                domain={authContext.domain}
                clientId={authContext.clientId}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {/* ── Payment & Subscription section (shop owner / barber only) ── */}
        {showPaymentAndCancellation && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}
            >
              Billing &amp; Subscription
            </Typography>

            {/* Payment Details */}
            <Accordion
              expanded={expanded === 'panel-payment'}
              onChange={handleChange('panel-payment')}
              sx={{ mb: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-payment-content"
                id="panel-payment-header"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditCardIcon fontSize="small" color="action" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Payment Details
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <PaymentDetails />
              </AccordionDetails>
            </Accordion>

            {/* Cancel Subscription */}
            <Accordion
              expanded={expanded === 'panel-cancel'}
              onChange={handleChange('panel-cancel')}
              sx={{ mb: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-cancel-content"
                id="panel-cancel-header"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CancelOutlinedIcon fontSize="small" color="error" />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="error.main"
                  >
                    Cancel Subscription
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <CancelSubscription />
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Grid>
    </Container>
  );
}
